import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import Watermark from "../Watermark";

/* ----------------------------------------
   Reusable Section Card (Mobile-first)
---------------------------------------- */
const SectionCard = ({ title, desc, children }) => (
  <Paper
    elevation={0}
    sx={{
      position: "relative",
      overflow: "hidden", // ✅ IMPORTANT: clips accent rail

      p: { xs: 2.25, sm: 2.75, md: 3.5 },
      // borderRadius: { xs: 2.5, md: 3 },
      borderRadius: { xs: 1.5, md: 2 },

      background:
        "linear-gradient(180deg, #FFFFFF 0%, rgba(246,247,235,0.65) 100%)",

      border: "1px solid var(--color-midnight-15)",

      boxShadow:
        "0 12px 30px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.6)",

      mb: { xs: 2.5, md: 4 },
    }}
  >
    {/* ✅ Accent rail now perfectly clipped */}
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        background:
          "linear-gradient(180deg, rgba(233,79,55,0.55), rgba(233,79,55,0.15))",
      }}
    />

    {/* Content */}
    <Box sx={{ pl: 1.75 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: { xs: "0.95rem", md: "1rem" },
          mb: 0.3,
          color: "var(--color-midnight)",
        }}
      >
        {title}
      </Typography>

      {desc && (
        <Typography
          sx={{
            fontSize: "0.78rem",
            color: "var(--color-midnight-70)",
            mb: 1.8,
            lineHeight: 1.55,
          }}
        >
          {desc}
        </Typography>
      )}

      {children}
    </Box>
  </Paper>
);

/* ----------------------------------------
   STEP 2 COMPONENT
---------------------------------------- */
const Step2 = ({ data, onBack, onNext }) => {
  const [form, setForm] = useState({
    projectSize: data?.projectSize || "medium",
    pages: data?.pages || 15,
    designLevel: data?.designLevel || "standard",
    roles: data?.roles || [],
    content: data?.content || "partial",
    timeline: data?.timeline || "flexible",
  });

  const toggleRole = (role) => {
    setForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));
  };

  return (
    <Box position="relative" mt={{ xs: 4, md: 6 }}>
      {/* ================= HEADER ================= */}
      <Box
        textAlign="center"
        mb={{ xs: 4, md: 5 }}
        sx={{ maxWidth: 720, mx: "auto" }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "var(--color-midnight-60)",
            mb: 0.8,
          }}
        >
          Scope & Planning
        </Typography>

        <Typography
          component="h3"
          sx={{
            fontSize: {
              xs: "1.45rem",
              sm: "1.6rem",
              md: "1.85rem",
            },
            fontWeight: 800,
            lineHeight: 1.25,
            letterSpacing: "-0.015em",
            color: "var(--color-midnight)",
          }}
        >
          Project Scope &{" "}
          <Box component="span" sx={{ color: "var(--color-fiery)" }}>
            Complexity
          </Box>
        </Typography>

        <Typography
          sx={{
            mt: 1.2,
            fontSize: { xs: "0.9rem", md: "1.05rem" },
            lineHeight: 1.65,
            color: "var(--color-midnight-70)",
          }}
        >
          Tell us about scale, design depth, and delivery expectations so we can
          generate an accurate estimate.
        </Typography>

        {/* <Typography
          sx={{
            mt: 1,
            fontSize: "0.75rem",
            color: "var(--color-midnight-60)",
          }}
        >
          You can adjust everything later — nothing is final.
        </Typography> */}
      </Box>

      {/* ================= CONTENT ================= */}
      <Stack>
        {/* Project Size */}
        <SectionCard
          title="Project Size"
          desc="Overall scale and functional depth of the product"
        >
          <RadioGroup
            value={form.projectSize}
            onChange={(e) => setForm({ ...form, projectSize: e.target.value })}
          >
            {[
              ["small", "Small — Landing page, MVP, basic app"],
              [
                "medium",
                "Medium — Business website, dashboards, multi-page app",
              ],
              [
                "large",
                "Large — SaaS platform, marketplace, enterprise system",
              ],
            ].map(([value, label]) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio />}
                label={label}
                sx={{
                  width: "fit-content",
                  "& .MuiTypography-root": {
                    fontSize: { xs: "0.75rem", md: "0.95rem" },
                  },
                }}
              />
            ))}
          </RadioGroup>
        </SectionCard>

        {/* Screens / Pages */}
        <SectionCard
          title="Estimated Screens / Pages"
          desc="Approximate number of distinct screens or pages"
        >
          <Slider
            value={form.pages}
            min={5}
            max={60}
            step={5}
            valueLabelDisplay="on"
            marks={[
              { value: 5, label: "5" },
              { value: 15, label: "15" },
              { value: 30, label: "30" },
              { value: 60, label: "40+" },
            ]}
            sx={{
              "& .MuiSlider-valueLabel": {
                fontSize: "0.75rem",
                borderRadius: 2,
              },
            }}
            onChange={(_, value) => setForm({ ...form, pages: value })}
          />
        </SectionCard>

        {/* UI / UX */}
        <SectionCard
          title="UI / UX Design Level"
          desc="Visual polish and interaction complexity"
        >
          <RadioGroup
            value={form.designLevel}
            onChange={(e) => setForm({ ...form, designLevel: e.target.value })}
          >
            {[
              ["basic", "Basic — Template-based, minimal branding"],
              ["standard", "Standard — Custom UI, responsive, branded"],
              ["premium", "Premium — Advanced UX, animations, refined visuals"],
            ].map(([value, label]) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio />}
                label={label}
                sx={{
                  width: "fit-content",
                  "& .MuiTypography-root": {
                    fontSize: { xs: "0.75rem", md: "0.95rem" },
                  },
                }}
              />
            ))}
          </RadioGroup>
        </SectionCard>

        {/* Roles */}
        <SectionCard
          title="User Roles & Permissions"
          desc="Types of users who will access the system"
        >
          <FormGroup>
            {[
              ["public", "Public users"],
              ["users", "Registered users"],
              ["admin", "Admin dashboard"],
              ["multiple", "Multiple user roles"],
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={form.roles.includes(key)}
                    onChange={() => toggleRole(key)}
                  />
                }
                label={label}
                sx={{
                  width: "fit-content",
                  "& .MuiTypography-root": {
                    fontSize: { xs: "0.75rem", md: "0.95rem" },
                  },
                }}
              />
            ))}
          </FormGroup>
        </SectionCard>

        {/* Content */}
        <SectionCard
          title="Content Responsibility"
          desc="Who prepares text, images, and media"
        >
          <RadioGroup
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
          >
            {[
              ["client", "Content provided by your team"],
              ["partial", "Partial content support required"],
              ["full", "Full content & copywriting needed"],
            ].map(([value, label]) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio />}
                label={label}
                sx={{
                  width: "fit-content",
                  "& .MuiTypography-root": {
                    fontSize: { xs: "0.75rem", md: "0.95rem" },
                  },
                }}
              />
            ))}
          </RadioGroup>
        </SectionCard>

        {/* Timeline */}
        <SectionCard
          title="Expected Timeline"
          desc="Delivery urgency impacts team allocation"
        >
          <RadioGroup
            value={form.timeline}
            onChange={(e) => setForm({ ...form, timeline: e.target.value })}
          >
            {[
              ["flexible", "Flexible — Standard delivery"],
              ["priority", "Priority — Faster delivery"],
              ["urgent", "Urgent — Tight deadline"],
            ].map(([value, label]) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio />}
                label={label}
                sx={{
                  width: "fit-content",
                  "& .MuiTypography-root": {
                    fontSize: { xs: "0.75rem", md: "0.95rem" },
                  },
                }}
              />
            ))}
          </RadioGroup>
        </SectionCard>
      </Stack>

      {/* ================= ACTION BAR ================= */}
      <Paper
        elevation={0}
        sx={{
          mt: { xs: 3, md: 5 },
          p: { xs: 2, md: 2 },
          borderRadius: 2,
          backgroundColor: "rgba(246,247,235,0.6)",
          border: "1px solid var(--color-midnight-15)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          {/* Back Button */}
          <Button
            variant="outlined"
            fullWidth
            sx={{ maxWidth: { sm: 160 } }}
            onClick={onBack}
          >
            Back
          </Button>

          {/* Primary CTA */}
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              maxWidth: { sm: 260 },
              fontWeight: 600,
              boxShadow: "0 10px 24px rgba(233,79,55,0.35)",
              position: "relative",
              overflow: "hidden",

              /* 🔥 Subtle pulse */
              animation: "ctaPulse 2.6s ease-in-out infinite",

              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
            onClick={() => onNext(form)}
          >
            Continue to Features
          </Button>
        </Stack>
      </Paper>

      {/* ===== Watermark ===== */}
      <Watermark />
    </Box>
  );
};

export default Step2;
